// Copyright (c) StrangeBee 2023

import { useCallback, useMemo } from 'react';
import moment from 'moment';
import { MessageDescriptor, useIntl } from 'react-intl';

interface DynamicObjectType<T> {
    [attribute: string]: T;
}

export type ParamsType = Record<string, string | number>;

export type UseLocalMessageType = (message: string | MessageDescriptor, params?: ParamsType) => string;

export const useLocalMessage = (): UseLocalMessageType => {
    const intl = useIntl();

    return useCallback(
        (message: string | MessageDescriptor, params?: ParamsType): string => {
            let opt = {};
            if (typeof message === 'string') {
                opt = { id: message };
            } else {
                opt = message;
            }

            return intl.formatMessage(opt, params);
        },
        [intl],
    );
};

export const convertToLocaleId = (key: string): string =>
    key
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/[\s_]+/g, '-')
        .toLowerCase();

export const useLocalDico = (arr: string[], translate: UseLocalMessageType): DynamicObjectType<string> => {
    return useMemo(
        () => arr.reduce((g: DynamicObjectType<string>, c: string) => ({ ...g, [c]: translate(c) }), {}),
        [translate, arr],
    );
};

export type UseLocalDateType = (timestamp: number) => string;
interface UseLocalDateOptions {
    format: string;
}
const defaultUseLocalOpts: UseLocalDateOptions = {
    format: 'LLLL',
};
export const useLocalDate = (useLocalDateOpts?: UseLocalDateOptions): UseLocalDateType => {
    moment.locale(navigator.language.split(/[-_]/)[0]);
    const opts = { ...defaultUseLocalOpts, ...(useLocalDateOpts || {}) };
    return (timestamp: number): string => moment(timestamp).format(opts.format);
};
